import { DisconnectReason } from 'livekit-client';

import type { CallPhase } from '#features/calls/model/callStore';

/** Что сказать человеку, когда медиасервер разорвал голос не по его нажатию. */
export type DisconnectOutcome = 'call' | 'otherTab' | 'silent' | 'removed' | 'lost';

/**
 * Разбор разрыва голоса, пришедшего не от своего выхода (свой выход сюда не доходит: эффект
 * помечает себя `disposed` до вызова disconnect).
 *
 * Порядок – смысл: звонок проверяется первым, потому что при нём сервер выселяет из комнаты тем
 * же способом, что и модерация, и по одной причине разрыва их не отличить.
 */
export const resolveDisconnectOutcome = (
  reason: DisconnectReason | undefined,
  callPhase: CallPhase,
  /**
   * Мы сами уходим отсюда, потому что входим в другую комнату.
   *
   * Сервер выводит из прежней тем же способом, каким хост выгоняет нарушителя, и по одной
   * причине разрыва их не отличить - знает об этом только клиент, который сам же и попросился
   * в другую комнату (см. `voiceRoomSwitch`).
   */
  switchingRoom = false
): DisconnectOutcome => {
  /*
    Нас вывел из комнаты личный звонок. Сервер при старте и ответе на звонок сам выселяет человека
    из всех голосовых комнат (`leaveAllRoomsForCall`), и разрыв доезжает раньше, чем арбитраж на
    клиенте успевает выйти сам, – арбитраж тогда видит, что голоса уже нет, и молчит. До этой
    ветки такой разрыв записывался «связь потеряна»: после звонка ярус пугал обрывом, хотя голос
    забрал звонок (прогон на телефоне 14.09.2026).
  */
  if (callPhase === 'incoming' || callPhase === 'outgoing' || callPhase === 'active') {
    return 'call';
  }

  // Медиасервер различает участников по identity, а она у голоса сессии одна на человека
  // (02.10.2026): вход в ту же комнату с другого устройства или из соседней вкладки выбивает
  // это подключение – последнее устройство забирает голос. Без объяснения это выглядит как «голос
  // сам отвалился», хотя разговор жив – просто на другом устройстве.
  if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
    return 'otherTab';
  }

  // Комнату закрыл наш же сервер, когда завершилась сессия. Это штатный конец разговора, а не
  // поломка: WS-кадр `VOICE_ROOM_CLOSED` обновит карточки, и виджету достаточно тихо уйти.
  // Ошибку тут показывать нельзя – каждое нормальное завершение сессии кончалось бы «связь
  // потеряна».
  if (reason === DisconnectReason.ROOM_DELETED) {
    return 'silent';
  }

  /*
    Из комнаты вывел сервер: хост выгнал.

    Это не обрыв связи, и говорить о нём словами обрыва нельзя: человек читал «Потеряна связь» с
    кнопкой «Вернуться», жал её и только тогда узнавал, что его удалили. Два противоречащих друг
    другу сообщения об одном событии.

    Остальные случаи этой же причины сюда не доходят: выселение ради звонка отсекается первой
    веткой, а переход в другую комнату - меткой ниже.

    🔴 На метку полагаться пришлось: допущение «клиент сам ставит новую комнату раньше, чем
    приедет разрыв старой» не выполнялось, и обычный переход между голосами двух сессий показывал
    на карточке прежней «вас выгнали». Порядок двух асинхронных событий не гарантирован никем.
  */
  if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
    return switchingRoom ? 'silent' : 'removed';
  }

  // Всё остальное – разрыв не по воле человека: пропала сеть, медиасервер закрыл комнату.
  // Молчать нельзя: иначе голос просто исчезал, и человек не понимал, почему его перестали
  // слышать. Проверено на устройстве 27.08 обрывом сети.
  return 'lost';
};
